import React, { useState, useContext } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../App';
import { 
  Truck, 
  ChevronRight, 
  MapPin, 
  User, 
  Phone, 
  Mail, 
  FileText,
  CheckCircle,
  ArrowRight,
  Shield,
  Clock,
  Loader2,
  Calendar,
  Settings,
  Gauge,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';

const DemandeVisite = () => {
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Pre-fill type from URL params
  const typeFromUrl = searchParams.get('type') || '';
  
  const [formData, setFormData] = useState({
    // Contact
    nom: '',
    entreprise: '',
    telephone: '',
    email: '',
    // Location
    adresse: '',
    code_postal: '',
    ville: '',
    // Equipment
    type_materiel: typeFromUrl || '',
    marque: '',
    modele: '',
    annee: '',
    heures: '',
    etat: '',
    // Visit
    disponibilites: '',
    commentaires: '',
    rgpd_accepted: false
  });

  const typesMateriel = [
    { value: 'foreuse', label: 'Foreuse' },
    { value: 'sondeuse', label: 'Sondeuse' },
    { value: 'micropieu', label: 'Machine à micropieux' },
    { value: 'injection', label: 'Matériel d\'injection' },
    { value: 'instrumentation', label: 'Instrumentation / Mesure' },
    { value: 'autre', label: 'Autre matériel' }
  ];

  const etatsOptions = [
    { value: 'excellent', label: 'Excellent — carnet complet, révisions à jour' },
    { value: 'bon', label: 'Bon — entretien suivi, usure normale' },
    { value: 'moyen', label: 'Moyen — entretien partiel, travaux à prévoir' },
    { value: 'repar', label: 'À réviser — réparations importantes' }
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nom.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }
    if (!formData.telephone.trim()) {
      toast.error('Le téléphone est obligatoire');
      return;
    }
    if (!formData.code_postal.trim()) {
      toast.error('Le code postal est obligatoire');
      return;
    }
    if (!formData.ville.trim()) {
      toast.error('La ville est obligatoire');
      return;
    }
    if (!formData.type_materiel) {
      toast.error('Veuillez sélectionner le type de matériel');
      return;
    }
    if (!formData.rgpd_accepted) {
      toast.error('Veuillez accepter la politique de confidentialité');
      return;
    }

    setLoading(true);
    
    try {
      const form = new FormData();
      // Contact
      form.append('nom', formData.nom.trim());
      form.append('entreprise', formData.entreprise.trim());
      form.append('telephone', formData.telephone.trim());
      if (formData.email) form.append('email', formData.email.trim());
      // Location
      form.append('adresse', formData.adresse.trim());
      form.append('code_postal', formData.code_postal.trim());
      form.append('ville', formData.ville.trim());
      // Equipment
      form.append('type_materiel', formData.type_materiel);
      form.append('marque', formData.marque.trim());
      form.append('modele', formData.modele.trim());
      form.append('annee', formData.annee.trim());
      form.append('heures', formData.heures.trim());
      form.append('etat', formData.etat);
      // Visit
      form.append('disponibilites', formData.disponibilites.trim());
      form.append('commentaires', formData.commentaires.trim());
      form.append('rgpd_accepted', formData.rgpd_accepted);

      await axios.post(`${API}/demandes/visite-rachat`, form);
      
      setSubmitted(true);
      toast.success('Demande de visite envoyée avec succès !');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Demande envoyée | GEOTS</title>
        </Helmet>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-[#0A2540] mb-4">
              Demande de visite envoyée !
            </h1>
            <p className="text-gray-600 mb-6">
              Un expert GEOTS vous contactera dans les plus brefs délais pour organiser 
              la visite et l&apos;évaluation de votre matériel.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-blue-800 mb-2">Prochaines étapes :</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Un commercial vous appelle sous 48h</li>
                <li>• Planification d&apos;une visite sur site</li>
                <li>• Inspection et évaluation du matériel</li>
                <li>• Proposition de rachat ferme</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3">
              <Link 
                to="/"
                className="bg-[#635BFF] hover:bg-[#5248e5] text-white py-3 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              >
                Retour à l&apos;accueil
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/calcul/estimer-ma-foreuse"
                className="text-[#635BFF] hover:text-[#5248e5] font-medium"
              >
                ← Retour à l&apos;estimateur
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Demande de visite pour rachat | GEOTS</title>
        <meta name="description" content="Demandez une visite d'un expert GEOTS pour évaluer votre matériel de forage et recevoir une offre de rachat ferme." />
        <link rel="canonical" href="https://geots.consulting/rachat/visite" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/calcul/estimer-ma-foreuse" className="hover:text-[#635BFF]">Estimer ma foreuse</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Demande de visite</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[#635BFF] p-2 rounded-lg">
                  <Truck className="w-6 h-6" />
                </div>
                <span className="bg-[#635BFF]/20 text-[#00D4FF] text-xs font-bold px-3 py-1 rounded-full">
                  RACHAT DIRECT
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                Demande de visite pour rachat
              </h1>
              <p className="text-lg text-gray-300">
                Un expert GEOTS se déplace pour inspecter votre matériel et vous faire une offre de rachat ferme.
              </p>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Benefits */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-xl p-4 border flex items-start gap-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A2540]">Gratuit</p>
                  <p className="text-sm text-gray-500">Sans engagement</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border flex items-start gap-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A2540]">Sous 48h</p>
                  <p className="text-sm text-gray-500">Prise de contact</p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border flex items-start gap-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-[#0A2540]">Offre ferme</p>
                  <p className="text-sm text-gray-500">Après inspection</p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
              {/* Section 1: Contact */}
              <div className="p-6 border-b">
                <h2 className="text-lg font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#635BFF]" />
                  Vos coordonnées
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entreprise
                    </label>
                    <input
                      type="text"
                      name="entreprise"
                      value={formData.entreprise}
                      onChange={handleChange}
                      placeholder="Nom de votre entreprise"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jean@exemple.fr"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Section 2: Location */}
              <div className="p-6 border-b">
                <h2 className="text-lg font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#635BFF]" />
                  Lieu de la visite
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      name="adresse"
                      value={formData.adresse}
                      onChange={handleChange}
                      placeholder="123 rue de l'exemple"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Code postal <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="code_postal"
                        value={formData.code_postal}
                        onChange={handleChange}
                        placeholder="75001"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ville <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        placeholder="Paris"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Equipment */}
              <div className="p-6 border-b">
                <h2 className="text-lg font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#635BFF]" />
                  Matériel à évaluer
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type de matériel <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type_materiel"
                      value={formData.type_materiel}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent bg-white"
                      required
                    >
                      <option value="">Sélectionnez...</option>
                      {typesMateriel.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                      <input
                        type="text"
                        name="marque"
                        value={formData.marque}
                        onChange={handleChange}
                        placeholder="ex. Comacchio"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                      <input
                        type="text"
                        name="modele"
                        value={formData.modele}
                        onChange={handleChange}
                        placeholder="ex. MC 900"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                      <input
                        type="text"
                        name="annee"
                        value={formData.annee}
                        onChange={handleChange}
                        placeholder="ex. 2019"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Heures de forage</label>
                      <input
                        type="text"
                        name="heures"
                        value={formData.heures}
                        onChange={handleChange}
                        placeholder="ex. 7000"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">État général</label>
                    <select
                      name="etat"
                      value={formData.etat}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent bg-white"
                    >
                      <option value="">Sélectionnez...</option>
                      {etatsOptions.map(etat => (
                        <option key={etat.value} value={etat.value}>{etat.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Section 4: Visit */}
              <div className="p-6 border-b">
                <h2 className="text-lg font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#635BFF]" />
                  Disponibilités
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vos disponibilités pour la visite
                    </label>
                    <textarea
                      name="disponibilites"
                      value={formData.disponibilites}
                      onChange={handleChange}
                      placeholder="Ex: Lundi à vendredi de 8h à 17h, sauf mercredi..."
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Commentaires additionnels
                    </label>
                    <textarea
                      name="commentaires"
                      value={formData.commentaires}
                      onChange={handleChange}
                      placeholder="Informations complémentaires sur le matériel, raison de la vente..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="p-6 bg-gray-50">
                <div className="mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="rgpd_accepted"
                      checked={formData.rgpd_accepted}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 text-[#635BFF] border-gray-300 rounded focus:ring-[#635BFF]"
                    />
                    <span className="text-sm text-gray-600">
                      J&apos;accepte que mes données soient traitées conformément à la{' '}
                      <Link to="/politique-confidentialite" className="text-[#635BFF] hover:underline">
                        politique de confidentialité
                      </Link>
                      . <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#635BFF] hover:bg-[#5248e5] text-white py-4 px-6 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Truck className="w-5 h-5" />
                      Demander une visite
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center mt-3">
                  Un expert vous contactera sous 48h pour planifier la visite.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemandeVisite;
